import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableRow,
  TableCell,
  Box,
  Tooltip,
} from "@mui/material";
import { DragHandle, ExpandMore, Phone } from "@mui/icons-material";
import PATAddButton from "../../../components/Buttons/PATAddButton";
import PATEditButton from "../../../components/Buttons/PATEditButton";
import PATDeleteButton from "../../../components/Buttons/PATDeleteButton";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";
import {
  bulkUpdateOrderOfContact,
  createPersonnelEmergencyContact,
  deletePersonnelEmergenyContact,
  getPersonnelEmergencyContacts,
  updatePersonnelEmergencyContact,
} from "../../../reducers/PersonnelEmergencyContactReducer";
import EmergencyContactDialog from "./EmergencyContactDialog";
import CustomTable from "../../../components/CustomTable";
import {
  formatPhoneNumber,
  mapDictionaryOptions,
} from "../../../utils/functions";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { setGlobalCircularLoading } from "../../../reducers/UiReducer";

const tableHeaders = [
  "Name",
  "Phone",
  "Cell Phone",
  "Relationship",
  "Address",
  "City",
  "State",
  "Zip",
  "Actions",
  "",
];

const EmergencyContactsTable = ({
  personnelId,
  stateOptions,
  cityOptions,
  personnelEmergencyContacts,
  theme,
  allExpanded = false,
  canAdd,
  canEdit,
  canDelete,
  readOnly,
}) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);
  const [openEmergencyContactDialog, setOpenEmergencyContactDialog] =
    useState(false);
  const [editingEmergencyContact, setEditingEmergencyContact] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [emergencyContactToDelete, setEmergencyContactToDelete] =
    useState(null);
  const [expanded, setExpanded] = useState(allExpanded);
  const [isLoadData, setIsLoadData] = useState(false);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const relationshipOptions = mapDictionaryOptions(
    dictionary.codePerRelationships
  );

  useEffect(() => {
    setExpanded(allExpanded);
  }, [allExpanded]);

  useEffect(() => {
    if (personnelId && expanded && !isLoadData && personnelEmergencyContacts?.length === 0) {
      dispatch(getPersonnelEmergencyContacts(personnelId));
      setIsLoadData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personnelId, personnelEmergencyContacts, expanded, dispatch]);

  const handleOpenAddEmergencyContact = (e) => {
    e.stopPropagation();
    setEditingEmergencyContact(null);
    setOpenEmergencyContactDialog(true);
  };

  const handleOpenEmergencyContact = (e, ec) => {
    e.stopPropagation();
    setEditingEmergencyContact(ec);
    setOpenEmergencyContactDialog(true);
  };

  const handleSaveEmergencyContact = (data) => {
    if (editingEmergencyContact) {
      dispatch(
        updatePersonnelEmergencyContact(editingEmergencyContact.id, {
          ...data,
          personnelId: personnelId,
        })
      ).then(() => dispatch(getPersonnelEmergencyContacts(personnelId)));
    } else {
      dispatch(
        createPersonnelEmergencyContact({
          ...data,
          personnelId: personnelId,
        })
      ).then(() => dispatch(getPersonnelEmergencyContacts(personnelId)));
    }
  };

  const handleOpenDeleteDialog = (e, ecId) => {
    e.stopPropagation();
    setEmergencyContactToDelete(ecId);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (emergencyContactToDelete) {
      dispatch(
        deletePersonnelEmergenyContact(
          emergencyContactToDelete,
          userData?.user?.ptsUserID,
          personnelId
        )
      ).then(() => dispatch(getPersonnelEmergencyContacts(personnelId)));
    }
    setOpenDeleteDialog(false);
    setEmergencyContactToDelete(null);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    dispatch(setGlobalCircularLoading(true));

    const items = Array.from(personnelEmergencyContacts);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    const updated = items.map((row, index) => ({
      ...row,
      orderOfContact: index + 1,
    }));

    try {
      await dispatch(
        bulkUpdateOrderOfContact(updated, personnelId, userData?.user?.ptsUserID)
      );
    } catch (e) {
      dispatch(setGlobalCircularLoading(false));
    }
    finally {
      dispatch(setGlobalCircularLoading(false));
    }
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: theme.accordion.backgroundColor,
            color: theme.accordion.textColor,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" alignItems="center">
              <Phone sx={{ mr: 1 }} />
              <span>Emergency Contacts</span>
            </Box>
            <Box sx={{ mr: 2 }}>
              {!readOnly && canAdd && (
                <PATAddButton
                  tooltipText="Add Emergency Contact"
                  onClick={handleOpenAddEmergencyContact}
                />
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <DragDropContext onDragEnd={!readOnly ? handleDragEnd : undefined}>
            <Droppable droppableId="table">
              {(provided) => (
                personnelEmergencyContacts?.length > 0 ? (
                <CustomTable
                  tableDroppableProps={{...provided.droppableProps}}
                  tableRef={provided.innerRef}
                  draggablePlaceholder={provided.placeholder}
                  columns={tableHeaders}
                  data={personnelEmergencyContacts}
                  renderRow={(ec, index) => (
                    <Draggable
                      key={ec.id?.toString()}
                      draggableId={ec.id?.toString()}
                      index={index}
                      isDragDisabled={readOnly}
                    >
                      {(provided, snapshot) => (
                        <TableRow 
                          key={index} 
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            backgroundColor: snapshot.isDragging ? '#E3F2FD' : 'inherit',
                            boxShadow: snapshot.isDragging ? 3 : 0,
                            transition: 'all 0.2s ease',
                          }}
                          style={{
                            ...provided.draggableProps.style,
                            transform: provided.draggableProps.style?.transform
                              ? provided.draggableProps.style.transform.replace(
                                  /translate3d\(([^,]+),\s*([^)]+)\)/,
                                  (match, x, y, z) => {
                                    return `translate3d(${x}, calc(${y} - 50px), ${z})`;
                                  }
                                )
                              : undefined,
                          }}
                        > 
                          <TableCell>{ec.name}</TableCell>
                          <TableCell>{formatPhoneNumber(ec.phone)}</TableCell>
                          <TableCell>{formatPhoneNumber(ec.cellPhone)}</TableCell>
                          <TableCell>
                            {ec.relationshipInfo?.description || ec.relationship}
                          </TableCell>
                          <TableCell>{ec.address}</TableCell>
                          <TableCell>{ec.city}</TableCell>
                          <TableCell>{ec.stateInfo?.description || ec.state}</TableCell>
                          <TableCell>{ec.zip}</TableCell>
                          <TableCell>
                            <Box display="flex" gap={1}>
                              {!readOnly && canEdit && (
                                <PATEditButton
                                  tooltipText="Edit Emergency Contact"
                                  onClick={(e) => handleOpenEmergencyContact(e, ec)}
                                  size="20px"
                                />
                              )}
                              {!readOnly && canDelete && (
                                <PATDeleteButton
                                  tooltipText="Delete Emergency Contact"
                                  onClick={(e) => handleOpenDeleteDialog(e, ec.id)}
                                  size="20px"
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell
                            {...(!readOnly ? provided.dragHandleProps : {})}
                            sx={{
                              width: '40px',
                              cursor: readOnly ? 'default' : 'grab',
                              color: 'grey',
                            }}
                          >
                            <Tooltip title={readOnly ? "Read-only mode" : "Click to drag and reorder"}>
                              <DragHandle fontSize="small" />
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  )}
                  isEnableFitContentOnActions
                />
                ) : (
                  <span>No Emergency Contacts Available</span>
                )
              )}
            </Droppable>
          </DragDropContext>
        </AccordionDetails>
      </Accordion>
      <EmergencyContactDialog
        open={openEmergencyContactDialog}
        onClose={() => setOpenEmergencyContactDialog(false)}
        onSave={handleSaveEmergencyContact}
        initialData={editingEmergencyContact}
        relationshipOptions={relationshipOptions}
        stateOptions={stateOptions}
        cityOptions={cityOptions}
      />
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setEmergencyContactToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Personnel Emergency Contact"
        message="Are you sure you want to delete this personnel emergency contact?"
      />
    </>
  );
};

export default EmergencyContactsTable;
